import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { changePassword } from "../api/account";
import { performLogout } from "../services/logoutService";
import { Button } from "@t-one-internal/design-system";
import { Input } from "@t-one-internal/design-system";
import { Label } from "@t-one-internal/design-system";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@t-one-internal/design-system";
import { Eye, EyeOff, ArrowLeft, CheckCircle } from "lucide-react";

export default function ChangePasswordPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    feedback: string;
    color: string;
  }>({ score: 0, feedback: "", color: "bg-gray-200" });
  const [validationErrors, setValidationErrors] = useState({
    minLength: false,
    maxLength: true,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    noRepeatedChars: true,
    noSequentialChars: true,
  });

  const validatePassword = (password: string) => {
    const hasRepeated = /(.)\1{2,}/.test(password);
    const hasSequential = (() => {
      for (let i = 0; i < password.length - 2; i++) {
        const a = password.charCodeAt(i);
        const b = password.charCodeAt(i + 1);
        const c = password.charCodeAt(i + 2);
        if ((b - a === 1 && c - b === 1) || (a - b === 1 && b - c === 1)) {
          return true;
        }
      }
      return false;
    })();

    const validations = {
      minLength: password.length >= 12,
      maxLength: password.length <= 128,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      noRepeatedChars: !hasRepeated,
      noSequentialChars: !hasSequential,
    };

    setValidationErrors(validations);

    const passedValidations =
      Object.values(validations).filter(Boolean).length;
    let strength = { score: 0, feedback: "", color: "bg-gray-200" };

    if (password.length === 0) {
      strength = { score: 0, feedback: "", color: "bg-gray-200" };
    } else if (passedValidations <= 3) {
      strength = { score: 25, feedback: "Weak", color: "bg-red-500" };
    } else if (passedValidations <= 5) {
      strength = { score: 50, feedback: "Fair", color: "bg-orange-500" };
    } else if (passedValidations <= 7) {
      strength = { score: 75, feedback: "Good", color: "bg-yellow-500" };
    } else {
      strength = { score: 100, feedback: "Strong", color: "bg-green-500" };
    }

    setPasswordStrength(strength);
    return Object.values(validations).every(Boolean);
  };

  const handleNewPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, newPassword });
    validatePassword(newPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.oldPassword) {
      setError("Please enter your current password.");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (formData.oldPassword === formData.newPassword) {
      setError("New password must be different from your current password.");
      return;
    }

    if (!validatePassword(formData.newPassword)) {
      setError("Password does not meet all requirements.");
      return;
    }

    setIsSubmitting(true);

    try {
      await changePassword({
        old_password: formData.oldPassword,
        new_password: formData.newPassword,
      });
      setIsSuccess(true);
    } catch (error: any) {
      console.error("Error changing password:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to change password. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const timer = setTimeout(() => {
        performLogout();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isSuccess]);

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900">
              Password Changed
            </h2>
            <p className="text-gray-600 text-sm">
              Your password has been changed successfully. You will be logged out shortly...
            </p>
            <Button
              onClick={() => performLogout()}
              className="w-full h-11"
              style={{ backgroundColor: "#4B45E5" }}
            >
              Logout Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="space-y-1 pb-6">
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
          </div>
          <div className="flex justify-center mb-4">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "#4B45E5" }}
            >
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-center text-gray-900">
            Change Password
          </CardTitle>
          <CardDescription className="text-center text-gray-600">
            Enter your current password and choose a new one
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Old Password */}
            <div className="space-y-2">
              <Label
                htmlFor="oldPassword"
                className="text-sm font-medium text-gray-700"
              >
                Current Password
              </Label>
              <div className="relative">
                <Input
                  id="oldPassword"
                  name="oldPassword"
                  type={showOldPassword ? "text" : "password"}
                  placeholder="Enter your current password"
                  value={formData.oldPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, oldPassword: e.target.value })
                  }
                  className="h-11 pr-10"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showOldPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label
                htmlFor="newPassword"
                className="text-sm font-medium text-gray-700"
              >
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter your new password"
                  value={formData.newPassword}
                  onChange={handleNewPasswordChange}
                  className="h-11 pr-10"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showNewPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>

              {formData.newPassword && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Password strength</span>
                    <span className="font-medium text-gray-700">
                      {passwordStrength.feedback}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${passwordStrength.score}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your new password"
                  value={formData.confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="h-11 pr-10"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showConfirmPassword ? (
                    <EyeOff size={20} />
                  ) : (
                    <Eye size={20} />
                  )}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            {formData.newPassword && (
              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2">
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Password must contain:
                </p>
                <div className="space-y-1">
                  {(
                    [
                      {
                        key: "minLength" as const,
                        label: "At least 12 characters",
                      },
                      {
                        key: "maxLength" as const,
                        label: "No more than 128 characters",
                      },
                      {
                        key: "hasUppercase" as const,
                        label: "One uppercase letter",
                      },
                      {
                        key: "hasLowercase" as const,
                        label: "One lowercase letter",
                      },
                      { key: "hasNumber" as const, label: "One number" },
                      {
                        key: "hasSpecialChar" as const,
                        label: "One special character",
                      },
                      {
                        key: "noRepeatedChars" as const,
                        label: "No 3+ repeated characters (e.g., aaa)",
                      },
                      {
                        key: "noSequentialChars" as const,
                        label:
                          "No 3+ sequential characters (e.g., abc, 123)",
                      },
                    ] as const
                  ).map(({ key, label }) => (
                    <div key={key} className="flex items-center text-xs">
                      <span
                        className={`mr-2 ${validationErrors[key] ? "text-green-600" : "text-gray-400"}`}
                      >
                        {validationErrors[key] ? "\u2713" : "\u25CB"}
                      </span>
                      <span
                        className={
                          validationErrors[key]
                            ? "text-gray-700"
                            : "text-gray-500"
                        }
                      >
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11"
              style={{ backgroundColor: "#4B45E5" }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Changing Password..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
